import React, { useMemo } from 'react';
import { Box, Collapse, Stack, TextField, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NumericFormat } from 'react-number-format';
import { currencyUtils } from 'skipify-types';

import { CallbackResponse, ConfigResponse } from '../../../store/modules/merchant/types';
import { ConfigSection, ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import {
  getCallbackDisplayLabel,
  inheritableLabelsFromConfigNames,
  uniqueDisplayLabels,
} from '../../../util/parentSettingSave';
import { sanitizeNumericString } from '../../../util/helpers';

const validateInputNumber = (value?: string | null) => {
  if (!value) return true;
  const number = Number(value.replaceAll(/[^\d-]/g, ''));
  return !Number.isNaN(number) && number > 0;
};

type AgenticCheckoutValue = {
  maxTransactionAmount?: number;
  maxLookupShopperCalls?: number;
  maxGeneratePaylinkCalls?: number;
};

type Props = {
  agenticCheckoutConfig?: ConfigResponse<AgenticCheckoutValue>;
  callbacks?: CallbackResponse[];
  onSave: (config: {
    config: AgenticCheckoutValue;
    callback: { isEnabled: boolean; validateTotalCallbackUrl?: string };
  }) => Promise<void>;
  isSaving?: boolean;
  readOnly?: boolean;
};

const validationSchema = z.object({
  maxTransactionAmount: z
    .string()
    .optional()
    .refine((value) => validateInputNumber(value), { message: 'Invalid amount' }),
  maxLookupShopperCalls: z
    .string()
    .optional()
    .refine((value) => validateInputNumber(value), { message: 'Invalid number of calls' }),
  maxGeneratePaylinkCalls: z
    .string()
    .optional()
    .refine((value) => validateInputNumber(value), { message: 'Invalid number of calls' }),
  validateTotalCallbackUrl: z
    .string()
    .optional()
    .refine((value) => !value || value === '' || z.string().url().safeParse(value).success, {
      message: 'Invalid URL',
    }),
  isValidateTotalCallbackEnabled: z.boolean(),
});

type FormValues = z.infer<typeof validationSchema>;

export const AgenticCheckoutForm = ({
  agenticCheckoutConfig,
  callbacks,
  onSave,
  readOnly = false,
  isSaving = false,
}: Props) => {
  const existingValue = useMemo(
    () => agenticCheckoutConfig?.value || {},
    [agenticCheckoutConfig?.value],
  );

  const validateTotalCallbackUrl = callbacks?.find(
    (cb) => cb.callbackType === 'AgenticCheckoutValidateTotal',
  )?.url;

  const formDefaultValues: FormValues = {
    maxTransactionAmount: existingValue.maxTransactionAmount
      ? currencyUtils.minorToMajor(existingValue.maxTransactionAmount).toFixed(2)
      : '',
    maxLookupShopperCalls: existingValue.maxLookupShopperCalls?.toString() ?? '',
    maxGeneratePaylinkCalls: existingValue.maxGeneratePaylinkCalls?.toString() ?? '',
    validateTotalCallbackUrl: validateTotalCallbackUrl ?? '',
    isValidateTotalCallbackEnabled: !!validateTotalCallbackUrl,
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, errors, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: formDefaultValues,
  });

  const onCancel = () => {
    reset(formDefaultValues);
  };

  const inheritableSaveBarLabels = useMemo(() => {
    if (!isDirty) {
      return [];
    }
    const labels: string[] = [];
    const isAgenticConfigDirty =
      dirtyFields.maxTransactionAmount ||
      dirtyFields.maxLookupShopperCalls ||
      dirtyFields.maxGeneratePaylinkCalls;
    if (isAgenticConfigDirty) {
      labels.push(...inheritableLabelsFromConfigNames(['AGENTIC_CHECKOUT']));
    }
    if (dirtyFields.validateTotalCallbackUrl || dirtyFields.isValidateTotalCallbackEnabled) {
      labels.push(getCallbackDisplayLabel('AgenticCheckoutValidateTotal'));
    }
    return uniqueDisplayLabels(labels);
  }, [
    dirtyFields.isValidateTotalCallbackEnabled,
    dirtyFields.maxGeneratePaylinkCalls,
    dirtyFields.maxLookupShopperCalls,
    dirtyFields.maxTransactionAmount,
    dirtyFields.validateTotalCallbackUrl,
    isDirty,
  ]);

  const onFormSubmit = async (data: FormValues) => {
    const value: {
      config: AgenticCheckoutValue;
      callback: { isEnabled: boolean; validateTotalCallbackUrl?: string };
    } = { config: {}, callback: { isEnabled: false } };

    if (data.maxTransactionAmount) {
      const amountNumber = Number(sanitizeNumericString(data.maxTransactionAmount));
      if (!isNaN(amountNumber) && amountNumber > 0) {
        value.config.maxTransactionAmount = amountNumber;
      }
    }
    if (data.maxLookupShopperCalls) {
      const callsNumber = Number(sanitizeNumericString(data.maxLookupShopperCalls));
      if (!isNaN(callsNumber) && callsNumber > 0) {
        value.config.maxLookupShopperCalls = callsNumber;
      }
    }
    if (data.maxGeneratePaylinkCalls) {
      const callsNumber = Number(sanitizeNumericString(data.maxGeneratePaylinkCalls));
      if (!isNaN(callsNumber) && callsNumber > 0) {
        value.config.maxGeneratePaylinkCalls = callsNumber;
      }
    }

    value.callback.isEnabled = data.isValidateTotalCallbackEnabled;
    if (data.validateTotalCallbackUrl !== undefined) {
      value.callback.validateTotalCallbackUrl = data.validateTotalCallbackUrl.trim();
    }

    await onSave({ config: value.config, callback: value.callback });
    reset(undefined, { keepValues: true });
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(onFormSubmit)} id="agentic-checkout-form">
        <ConfigSection
          title="Transaction Limits"
          description="Set the maximum transaction amount for checkouts and paylinks created via MCP."
          sx={{ mb: 4 }}
        >
          <Box sx={{ mt: 2, maxWidth: '400px' }}>
            <Controller
              name="maxTransactionAmount"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  customInput={TextField}
                  variant="outlined"
                  label="Max Transaction Amount"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                  allowNegative={false}
                  disabled={readOnly}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  id={field.name}
                  error={!!errors.maxTransactionAmount}
                  helperText={
                    errors.maxTransactionAmount?.message ||
                    'Maximum transaction amount for a checkout/paylink via MCP'
                  }
                />
              )}
            />
          </Box>
        </ConfigSection>

        <ConfigSection
          title="Tool Call Limits"
          description="Set the maximum number of tool calls allowed per session for different MCP tools."
          sx={{ mb: 4 }}
        >
          <Stack spacing={3} sx={{ mt: 2, maxWidth: '400px' }}>
            <Controller
              name="maxLookupShopperCalls"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Max Shopper Lookups"
                  id="maxLookupShopperCalls"
                  type="number"
                  disabled={readOnly}
                  helperText={
                    errors.maxLookupShopperCalls?.message ||
                    'Maximum allowed number of shopper lookups per session'
                  }
                  {...field}
                  error={!!errors.maxLookupShopperCalls}
                />
              )}
            />
            <Controller
              name="maxGeneratePaylinkCalls"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Max Generated Paylinks"
                  id="maxGeneratePaylinkCalls"
                  type="number"
                  disabled={readOnly}
                  helperText={
                    errors.maxGeneratePaylinkCalls?.message ||
                    'Maximum allowed number of created paylinks per session'
                  }
                  {...field}
                  error={!!errors.maxGeneratePaylinkCalls}
                />
              )}
            />
          </Stack>
        </ConfigSection>

        <Stack sx={{ maxWidth: '448px' }} spacing={2}>
          <ConfigSwitchSection
            title="Validate Total Callback"
            description={
              <>
                You can designate a URL below where we can perform a callback to validate the total
                of the order on the pay tool call.
                <b> Please make sure to use a valid protocol (http:// or https://)</b>
              </>
            }
            isChecked={watch('isValidateTotalCallbackEnabled')}
            onToggle={(newIsValidateTotalCallbackEnabled) => {
              setValue('isValidateTotalCallbackEnabled', newIsValidateTotalCallbackEnabled, {
                shouldDirty: true,
              });
            }}
            renderSwitchLabel={() => (
              <Typography component="span">
                {`Callback URL is currently `}
                <Typography sx={{ fontWeight: 'bold' }} component="span">
                  {`${watch('isValidateTotalCallbackEnabled') ? 'enabled ' : 'disabled '}`}
                </Typography>
              </Typography>
            )}
            disabled={readOnly}
          />
          <Collapse in={watch('isValidateTotalCallbackEnabled')}>
            <Box sx={{ mt: 2, maxWidth: '448px' }}>
              <Controller
                name="validateTotalCallbackUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Validate Total Callback URL"
                    id="validateTotalCallbackUrl"
                    type="text"
                    disabled={readOnly}
                    error={!!errors.validateTotalCallbackUrl}
                    helperText={
                      errors.validateTotalCallbackUrl?.message ||
                      'Callback URL to validate the total of the checkout/paylink.'
                    }
                    fullWidth
                    {...field}
                  />
                )}
              />
            </Box>
          </Collapse>
        </Stack>
      </Box>

      <ParentAwareSaveBar
        formId="agentic-checkout-form"
        show={isDirty && !readOnly}
        saveLoading={isSaving}
        onSave={handleSubmit(onFormSubmit)}
        onCancel={onCancel}
        inheritableSettingLabels={inheritableSaveBarLabels}
        disableSave={isSaving}
      />
    </>
  );
};
